import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { GoogleLogo, PaperPlaneTilt, LinkBreak } from "@phosphor-icons/react";

const MSG = { connected: ["success", "Gmail connected — client emails now go out from your mailbox"], denied: ["error", "Google sign-in was cancelled"], invalid_state: ["error", "Sign-in expired, please try again"], token_failed: ["error", "Google rejected the sign-in — check the redirect URI in Google Cloud"], no_refresh_token: ["error", "Google did not grant offline access — remove the app at myaccount.google.com/permissions and connect again"] };

export const GmailCard = () => {
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);
  const [params, setParams] = useSearchParams();

  const load = async () => { try { const { data } = await api.get("/gmail/status"); setSt(data); } catch { setSt({ configured: false }); } };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    const g = params.get("gmail");
    if (!g) return;
    const [kind, text] = MSG[g] || ["error", `Gmail: ${g}`];
    toast[kind](text, { duration: 8000 });
    params.delete("gmail"); setParams(params, { replace: true });
  }, [params, setParams]);

  const connect = async () => { try { const { data } = await api.get("/gmail/connect"); window.location.href = data.url; } catch (e) { toast.error(e?.response?.data?.detail || "Cannot start Google sign-in"); } };
  const disconnect = async () => { if (!window.confirm("Disconnect Gmail? Client emails will go back to the default sender.")) return; await api.post("/gmail/disconnect"); toast.success("Gmail disconnected"); load(); };
  const toggle = async (v) => { await api.post(`/gmail/toggle?enabled=${v}`); setSt({ ...st, enabled: v }); toast.success(v ? "Client emails will use Gmail" : "Gmail paused — using default sender"); };
  const test = async () => {
    setBusy(true);
    try { const { data } = await api.post("/gmail/test"); toast.success(`Test email sent to ${data.to} via ${data.via}`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Test failed", { duration: 8000 }); load(); } finally { setBusy(false); }
  };

  if (!st) return null;
  return (
    <div className="hp-card p-6 space-y-4" data-testid="gmail-card">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h3 className="font-semibold flex items-center gap-2"><GoogleLogo size={18} weight="bold" /> Send client emails from Gmail</h3>
        {st.connected && <div className="flex items-center gap-2 text-sm"><span className="text-muted-foreground">Active</span><Switch checked={st.enabled !== false} onCheckedChange={toggle} data-testid="gmail-enabled-switch" /></div>}
      </div>
      {!st.configured && <p className="text-sm text-amber-700" data-testid="gmail-not-configured">Google credentials are not set on this server.</p>}
      {st.configured && !st.connected && (
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">Connect once with <strong>ops@heliopolispharmaceuticals.com</strong>. Order, shipment, document and delivery emails to clients will then be sent from that mailbox, with replies landing in your inbox. Team alerts keep using the default sender.</p>
          <Button className="rounded-sm" onClick={connect} data-testid="gmail-connect-btn"><GoogleLogo size={16} className="mr-2" weight="bold" /> Connect Gmail</Button>
          <p className="text-xs text-muted-foreground">Redirect URI registered in Google Cloud must be: <code className="break-all" data-testid="gmail-redirect-uri">{st.redirect_uri}</code></p>
        </div>
      )}
      {st.connected && (
        <div className="space-y-3">
          <p className="text-sm" data-testid="gmail-connected-email">Connected as <strong>{st.email}</strong> · {st.sent_count || 0} email(s) sent{st.last_sent_at ? ` · last ${new Date(st.last_sent_at * 1000).toLocaleString()}` : ""}</p>
          {st.last_error && <p className="text-xs rounded-sm border border-red-200 bg-red-50 text-red-800 px-3 py-2" data-testid="gmail-last-error">Last error: {st.last_error} — emails fell back to the default sender.</p>}
          <div className="flex gap-2 flex-wrap">
            <Button size="sm" variant="outline" className="rounded-sm" onClick={test} disabled={busy} data-testid="gmail-test-btn"><PaperPlaneTilt size={14} className="mr-1.5" /> {busy ? "Sending…" : "Send me a test email"}</Button>
            <Button size="sm" variant="ghost" className="rounded-sm text-red-700" onClick={disconnect} data-testid="gmail-disconnect-btn"><LinkBreak size={14} className="mr-1.5" /> Disconnect</Button>
          </div>
        </div>
      )}
    </div>
  );
};
